import { useQuery } from '@tanstack/react-query';
import cytoscape, { type Core, type ElementDefinition } from 'cytoscape';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useCampaign, useCampaignId, useTypes } from '../hooks';
import { t } from '../i18n';
import { classNames } from '../util';

export function GraphPage() {
  const cid = useCampaignId();
  const navigate = useNavigate();
  const types = useTypes(cid);
  const graph = useQuery({ queryKey: ['graph', cid], queryFn: () => api.graph(cid) });
  const container = useRef<HTMLDivElement>(null);
  const cy = useRef<Core | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [showUnresolved, setShowUnresolved] = useState(false);
  const campaign = useCampaign(cid);
  const gm = !!campaign.data && campaign.data.role !== 'player';

  const usedTypes = useMemo(() => {
    const keys = new Set(graph.data?.nodes.map((n) => n.typeKey));
    return (types.data ?? []).filter((ty) => keys.has(ty.key));
  }, [graph.data, types.data]);

  const elements = useMemo<ElementDefinition[]>(() => {
    if (!graph.data) return [];
    const visible = new Set(graph.data.nodes.filter((n) => !hidden.has(n.typeKey)).map((n) => n.id));
    const els: ElementDefinition[] = [];
    for (const n of graph.data.nodes) {
      if (!visible.has(n.id)) continue;
      els.push({
        data: { id: n.id, label: n.title, color: types.byKey.get(n.typeKey)?.color ?? '#999', degree: n.degree },
      });
    }
    for (const e of graph.data.edges) {
      if (visible.has(e.source) && visible.has(e.target)) {
        els.push({ data: { id: `${e.source}-${e.target}`, source: e.source, target: e.target } });
      }
    }
    if (showUnresolved) {
      for (const u of graph.data.unresolved) {
        const id = `unresolved:${u.key}`;
        const sources = u.sources.filter((s) => visible.has(s));
        if (sources.length === 0) continue;
        els.push({ data: { id, label: u.label, color: '#555', degree: sources.length }, classes: 'ghost' });
        for (const s of sources) els.push({ data: { id: `${s}-${id}`, source: s, target: id }, classes: 'ghost' });
      }
    }
    return els;
  }, [graph.data, hidden, showUnresolved, types.byKey]);

  useEffect(() => {
    if (!container.current || elements.length === 0) return;
    const css = getComputedStyle(document.documentElement);
    const text = css.getPropertyValue('--text').trim() || '#ddd';
    const edge = css.getPropertyValue('--border').trim() || '#444';
    const instance = cytoscape({
      container: container.current,
      elements,
      minZoom: 0.15,
      maxZoom: 3,
      wheelSensitivity: 0.3,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': 'data(color)',
            label: 'data(label)',
            color: text,
            'font-size': 12,
            'font-family': 'Alegreya Sans, sans-serif',
            'text-outline-color': '#16100b',
            'text-outline-width': 2,
            'text-valign': 'bottom',
            'text-margin-y': 4,
            'text-wrap': 'ellipsis',
            'text-max-width': '140px',
            width: 'mapData(degree, 0, 20, 10, 42)',
            height: 'mapData(degree, 0, 20, 10, 42)',
          },
        },
        { selector: 'edge', style: { width: 1, 'line-color': edge, 'curve-style': 'straight', opacity: 0.8 } },
        { selector: '.ghost', style: { 'line-style': 'dashed', opacity: 0.5 } },
        { selector: 'node.ghost', style: { 'border-width': 1, 'border-style': 'dashed', 'border-color': '#888' } },
        { selector: '.faded', style: { opacity: 0.15 } },
        { selector: 'node.hl', style: { 'font-size': 14, 'font-weight': 'bold', 'border-width': 2, 'border-color': '#f0c46c' } },
      ],
      layout: {
        name: 'cose',
        animate: false,
        randomize: true,
        nodeRepulsion: () => 9000,
        idealEdgeLength: () => 70,
        gravity: 0.6,
        numIter: 2500,
        padding: 30,
      } as cytoscape.LayoutOptions,
    });
    instance.on('tap', 'node', (evt) => {
      const id = evt.target.id() as string;
      if (!id.startsWith('unresolved:')) navigate(`/c/${cid}/n/${id}`);
    });
    // Nachbarschaft beim Überfahren hervorheben
    instance.on('mouseover', 'node', (evt) => {
      const hood = evt.target.closedNeighborhood();
      instance.elements().not(hood).addClass('faded');
      evt.target.addClass('hl');
    });
    instance.on('mouseout', 'node', (evt) => {
      instance.elements().removeClass('faded');
      evt.target.removeClass('hl');
    });
    cy.current = instance;
    return () => instance.destroy();
  }, [elements, cid, navigate]);

  const toggle = (key: string) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setHidden(next);
  };

  return (
    <div className="graph-page">
      <div className="graph-toolbar">
        {usedTypes.map((ty) => (
          <button
            key={ty.key}
            className={classNames('chip', !hidden.has(ty.key) && 'on')}
            style={{ ['--chip' as string]: ty.color }}
            onClick={() => toggle(ty.key)}
          >
            {ty.label}
          </button>
        ))}
        {gm && (
          <label className="inline">
            <input type="checkbox" checked={showUnresolved} onChange={(e) => setShowUnresolved(e.target.checked)} />
            {t('graph.showUnresolved')}
          </label>
        )}
        <button onClick={() => cy.current?.fit(undefined, 30)}>{t('graph.fit')}</button>
      </div>
      {graph.isLoading && <div className="muted pad">{t('common.loading')}</div>}
      <div className="graph" ref={container} />
    </div>
  );
}
